'use client';

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Spinner, Alert, Badge, Card, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faUserDoctor,
  faAward,
  faBriefcase,
  faCalendar,
  faLocationDot,
  faFileAlt,
  faDownload,
  faTimes,
  faEye,
  faCertificate,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faShieldHalved,
  faIdCard,
  faCakeCandles,
  faVenusMars,
  faGraduationCap,
  faPrescriptionBottleMedical,
  faFileContract,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { getLocumById, toggleLocumStatus, getLocumBookings, approveLocum, rejectLocum } from 'features/admin/adminApi';
import { downloadDocument, viewDocument } from 'features/profile/profileApi';
import Button from 'components/base/Button';
import { deleteLocum } from 'features/auth/authApi';

const AdminLocumDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [locum, setLocum] = useState<any>(null);
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // FETCH LOCUM DETAILS (includes documents)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const locumData = await getLocumById(id || '');
        setLocum(locumData);

        // Fetch bookings
        try {
          setBookingsLoading(true);
          const bookingsData = await getLocumBookings(id || '');
          setBookings(bookingsData);
        } catch (bookingErr: any) {
          console.error('Error fetching bookings:', bookingErr.message);
        } finally {
          setBookingsLoading(false);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // TOGGLE STATUS
  const handleToggleStatus = async () => {
    try {
      await toggleLocumStatus(id || '');
      const updatedLocum = await getLocumById(id || '');
      setLocum(updatedLocum);
      setSuccess('Status updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);

      await deleteLocum(Number(id));

      setSuccess("Locum deleted successfully.");

      setShowDeleteModal(false);

      navigate("/dashboard/admin/user-management/manage-locum");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // HANDLE DOCUMENT DOWNLOAD
  const handleDocumentDownload = async (document: any) => {
    try {
      await downloadDocument(document.document_name);
      setSuccess('Document downloaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Failed to download document');
    }
  };

  // HANDLE DOCUMENT VIEW
  const handleDocumentView = (document: any) => {
    viewDocument(document.document_name);
  };

  if (loading) {
    return (
      <Container className="py-5">
        <Row className="py-5">
          <Col className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-body-tertiary">Loading locum details...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!locum) {
    return (
      <Container className="py-5">
        <Row className="py-5">
          <Col className="text-center">
            <Alert variant="danger">Locum not found</Alert>
            <Button
              variant="phoenix-primary"
              onClick={() => navigate('/dashboard/admin/user-management/manage-locum')}
            >
              Back to Manage Locums
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  const handleApprove = async () => {
    try {
      setApproveLoading(true);

      await approveLocum(id || '');

      const updatedLocum = await getLocumById(id || '');

      setLocum(updatedLocum);
      setShowApproveModal(false);

      setSuccess("Locum approved successfully");

      setTimeout(() => setSuccess(''), 3000);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setApproveLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      if (!rejectReason.trim()) {
        return;
      }

      setRejectLoading(true);

      await rejectLocum(id || "", rejectReason);

      const updatedLocum = await getLocumById(id || "");

      setLocum(updatedLocum);

      setShowRejectModal(false);
      setRejectReason("");

      setSuccess("Locum rejected successfully");

      setTimeout(() => setSuccess(""), 3000);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setRejectLoading(false);
    }
  };

  return (
    <Container className="py-2 py-lg-4">
      {/* Alerts */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              {error}
            </Alert>
          </Col>
        </Row>
      )}
      {success && (
        <Row className="mb-4">
          <Col>
            <Alert variant="success" onClose={() => setSuccess('')} dismissible>
              {success}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center gap-3 mb-3">
            <Button
              variant="phoenix-secondary"
              size="sm"
              onClick={() => navigate('/dashboard/admin/user-management/manage-locum')}
              startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
            >
              Back
            </Button>
          </div>
          <div className="d-flex align-items-center gap-3">
            <h2 className="text-body-emphasis mb-0">
              <FontAwesomeIcon icon={faUserDoctor} className="me-2 text-primary" />
              {locum.user.name} {locum.user.last_name}
            </h2>
            <Badge className={`rounded-pill ${locum.is_active ? 'bg-success' : 'bg-danger'}`}>
              {locum.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <p className="text-body-tertiary mt-2 mb-0">
            Admin Workspace • Locum Details
          </p>
        </Col>
      </Row>


      {/* Personal Information Card */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0">Personal Information</Card.Title>
            </Card.Header>
            <Card.Body>
              <Row className="g-4">
                {/* Email */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Email
                  </p>
                  <a
                    href={`mailto:${locum.user.email}`}
                    className="text-primary text-decoration-none fw-semibold"
                  >
                    {locum.user.email}
                  </a>
                </Col>

                {/* Phone */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Phone
                  </p>
                  <a
                    href={`tel:${locum.profile.phone}`}
                    className="text-primary text-decoration-none fw-semibold"
                  >
                    {locum.profile.phone}
                  </a>
                </Col>

                {/* National Insurance Number */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    National Insurance Number
                  </p>
                  <h6 className="text-body-emphasis fw-semibold mb-0">
                    {locum.profile.national_insurance_number || 'N/A'}
                  </h6>
                </Col>

                {/* Location */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Location
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="me-2 text-danger"
                    />
                    {locum.profile.location}
                  </h6>
                </Col>

                {/* Address */}
                <Col md={12}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Address
                  </p>
                  <h6 className="text-body-emphasis fw-semibold mb-0" style={{ whiteSpace: 'pre-line' }}>
                    {locum.profile.address || 'N/A'}
                  </h6>
                </Col>

                {/* City */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    City
                  </p>
                  <h6 className="text-body-emphasis fw-semibold mb-0">
                    {locum.profile.city || 'N/A'}
                  </h6>
                </Col>

                {/* Zip Code */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    POSTCODE
                  </p>
                  <h6 className="text-body-emphasis fw-semibold mb-0">
                    {locum.profile.zip_code || 'N/A'}
                  </h6>
                </Col>

                {/* Travel Distance */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Travel Distance
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    {locum.profile.travel_distance} miles
                  </h6>
                </Col>

                {/* Date of Birth */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Date of Birth
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faCakeCandles}
                      className="me-2 text-success"
                    />
                    {locum.profile.dob ? new Date(locum.profile.dob).toLocaleDateString('en-GB') : 'N/A'}
                  </h6>
                </Col>

                {/* Gender */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Gender
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faVenusMars}
                      className="me-2 text-info"
                    />
                    {locum.profile.gender ? locum.profile.gender.charAt(0).toUpperCase() + locum.profile.gender.slice(1) : 'N/A'}
                  </h6>
                </Col>

                {/* IR35 Compliance */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    IR35 Compliance
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faFileContract}
                      className="me-2 text-primary"
                    />
                    {locum.user.ir35 === 'true' || locum.user.ir35 === true ? (
                      <Badge bg="success" className="rounded-pill">Confirmed</Badge>
                    ) : (
                      <Badge bg="secondary" className="rounded-pill">Not Confirmed</Badge>
                    )}
                  </h6>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Professional Details Card */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">

              <Card.Title className="mb-0">
                <FontAwesomeIcon icon={faBriefcase} className="me-2 text-primary" />
                Professional Details
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Row className="g-4">
                {/* Qualifications */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Qualification
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faAward}
                      className="me-2 text-warning"
                    />
                    {locum.profile.qualifications}
                  </h6>
                </Col>

                {/* Role */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Locum Role
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faBriefcase}
                      className="me-2 text-primary"
                    />
                    {locum.profile.locum_role
                      ? locum.profile.locum_role.charAt(0).toUpperCase() +
                      locum.profile.locum_role.slice(1)
                      : ""}
                  </h6>
                </Col>

                {/* Experience */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Experience
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faCalendar}
                      className="me-2 text-info"
                    />
                    {locum.profile.experience_years} Years
                  </h6>
                </Col>

                {/* GPhC Number */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    GPhC Number
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faIdCard}
                      className="me-2 text-success"
                    />
                    {locum.profile.gphc_number || 'N/A'}
                  </h6>
                </Col>

                {/* Qualification Date */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Qualification Date
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faGraduationCap}
                      className="me-2 text-primary"
                    />
                    {locum.profile.qualification_date ? new Date(locum.profile.qualification_date).toLocaleDateString('en-GB') : 'N/A'}
                  </h6>
                </Col>

                {/* Independent Prescriber */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Independent Prescriber
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faPrescriptionBottleMedical}
                      className="me-2 text-danger"
                    />
                    {locum.profile.independent_prescriber === 1 || locum.profile.independent_prescriber === true ? (
                      <Badge bg="success" className="rounded-pill">Yes</Badge>
                    ) : (
                      <Badge bg="secondary" className="rounded-pill">No</Badge>
                    )}
                  </h6>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* References Card */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0">
                <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
                References
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Row className="g-4">
                {/* Reference 1 */}
                {locum.user.ref_Name_1 && (
                  <Col md={12}>
                    <div className="border rounded p-3">
                      <h6 className="text-primary mb-3">Reference 1</h6>
                      <Row className="g-3">
                        <Col md={4}>
                          <p className="text-body-tertiary small mb-1 text-uppercase fw-bold">
                            Name
                          </p>
                          <p className="text-body-emphasis fw-semibold mb-0">
                            {locum.user.ref_Name_1}
                          </p>
                        </Col>
                        <Col md={4}>
                          <p className="text-body-tertiary small mb-1 text-uppercase fw-bold">
                            Phone Number
                          </p>
                          <a
                            href={`tel:${locum.user.ref_PhoneNumber_1}`}
                            className="text-primary text-decoration-none fw-semibold"
                          >
                            {locum.user.ref_PhoneNumber_1}
                          </a>
                        </Col>
                        <Col md={4}>
                          <p className="text-body-tertiary small mb-1 text-uppercase fw-bold">
                            Details
                          </p>
                          <p className="text-body-emphasis fw-semibold mb-0">
                            {locum.user.ref_Details_1 || 'N/A'}
                          </p>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                )}

                {/* Reference 2 */}
                {locum.user.ref_Name_2 && (
                  <Col md={12}>
                    <div className="border rounded p-3">
                      <h6 className="text-primary mb-3">Reference 2</h6>
                      <Row className="g-3">
                        <Col md={4}>
                          <p className="text-body-tertiary small mb-1 text-uppercase fw-bold">
                            Name
                          </p>
                          <p className="text-body-emphasis fw-semibold mb-0">
                            {locum.user.ref_Name_2}
                          </p>
                        </Col>
                        <Col md={4}>
                          <p className="text-body-tertiary small mb-1 text-uppercase fw-bold">
                            Phone Number
                          </p>
                          <a
                            href={`tel:${locum.user.ref_PhoneNumber_2}`}
                            className="text-primary text-decoration-none fw-semibold"
                          >
                            {locum.user.ref_PhoneNumber_2}
                          </a>
                        </Col>
                        <Col md={4}>
                          <p className="text-body-tertiary small mb-1 text-uppercase fw-bold">
                            Details
                          </p>
                          <p className="text-body-emphasis fw-semibold mb-0">
                            {locum.user.ref_Details_2 || 'N/A'}
                          </p>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                )}

                {!locum.user.ref_Name_1 && !locum.user.ref_Name_2 && (
                  <Col md={12}>
                    <Alert variant="info" className="mb-0">
                      No references provided
                    </Alert>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Documents Card - SAME AS PROFILE PAGE */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0">
                <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
                Documents ({locum.documents?.length || 0})
              </Card.Title>
            </Card.Header>
            <Card.Body>
              {locum.documents && locum.documents.length > 0 ? (
                <div className="list-group">
                  {locum.documents.map((doc: any) => (
                    <div
                      key={doc.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <h6 className="mb-1 d-flex align-items-center">
                          <FontAwesomeIcon
                            icon={faCertificate}
                            className="me-2 text-warning"
                          />
                          {doc.doc_type.replace(/_/g, ' ').toUpperCase()}
                        </h6>
                        <small className="text-body-secondary">
                          {doc.document_name}
                        </small>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="phoenix-secondary"
                          size="sm"
                          startIcon={<FontAwesomeIcon icon={faEye} />}
                          onClick={() => handleDocumentView(doc)}
                        >
                          View
                        </Button>
                        <Button
                          variant="phoenix-primary"
                          size="sm"
                          startIcon={<FontAwesomeIcon icon={faDownload} />}
                          onClick={() => handleDocumentDownload(doc)}
                        >
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="info" className="mb-0">
                  No documents uploaded yet
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Bookings Card */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0">
                <FontAwesomeIcon icon={faBriefcase} className="me-2 text-primary" />
                Booking History ({bookings?.length || 0})
              </Card.Title>
            </Card.Header>
            <Card.Body>
              {bookingsLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <p className="text-body-tertiary">Loading bookings...</p>
                </div>
              ) : bookings && bookings.length > 0 ? (
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead className="bg-body-tertiary">
                      <tr>
                        <th className="text-body-emphasis fw-bold text-uppercase fs-10 ps-4 pe-4 py-3">
                          Pharmacy
                        </th>
                        <th className="text-body-emphasis fw-bold text-uppercase fs-10 ps-4 pe-4 py-3">
                          Date
                        </th>
                        <th className="text-body-emphasis fw-bold text-uppercase fs-10 ps-4 pe-4 py-3">
                          Time
                        </th>
                        <th className="text-body-emphasis fw-bold text-uppercase fs-10 ps-4 pe-4 py-3">
                          Location
                        </th>
                        <th className="text-body-emphasis fw-bold text-uppercase fs-10 ps-4 pe-4 py-3 text-center">
                          Pay Rate
                        </th>
                        <th className="text-body-emphasis fw-bold text-uppercase fs-10 ps-4 pe-4 py-3 text-center">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking: any) => (
                        <tr key={booking.booking_id} className="border-bottom border-translucent">
                          <td className="ps-4 pe-4 py-3">
                            <span className="text-body-emphasis fw-semibold">
                              {booking.pharmacy_name || 'N/A'}
                            </span>
                          </td>
                          <td className="ps-4 pe-4 py-3">
                            <span className="text-body">
                              <FontAwesomeIcon icon={faCalendar} className="me-2 text-info" />
                              {new Date(booking.date).toLocaleDateString()}
                            </span>
                          </td>
                          <td className="ps-4 pe-4 py-3">
                            <span className="text-body">
                              <FontAwesomeIcon icon={faClock} className="me-2 text-warning" />
                              {booking.start_time} - {booking.end_time}
                            </span>
                          </td>
                          <td className="ps-4 pe-4 py-3">
                            <span className="text-body-emphasis fw-semibold">
                              <FontAwesomeIcon
                                icon={faLocationDot}
                                className="me-2 text-danger"
                              />
                              {booking.shift_location}
                            </span>
                          </td>
                          <td className="ps-4 pe-4 py-3 text-center">
                            <Badge bg="success" className="rounded-pill">
                              £{booking.pay_rate}
                            </Badge>
                          </td>
                          <td className="ps-4 pe-4 py-3 text-center">
                            <Badge
                              className={`rounded-pill ${booking.status === 'confirmed'
                                ? 'bg-info'
                                : booking.status === 'completed'
                                  ? 'bg-success'
                                  : 'bg-danger'
                                }`}
                            >
                              <FontAwesomeIcon
                                icon={
                                  booking.status === 'confirmed'
                                    ? faClock
                                    : booking.status === 'completed'
                                      ? faCheckCircle
                                      : faTimesCircle
                                }
                                className="me-1"
                              />
                              {booking.status.charAt(0).toUpperCase() +
                                booking.status.slice(1)}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Alert variant="info" className="mb-0">
                  No bookings found
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Approval Status Card */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0">
                <FontAwesomeIcon
                  icon={faShieldHalved}
                  className="me-2 text-primary"
                />
                Approval Status
              </Card.Title>
            </Card.Header>

            <Card.Body>
              <Row className="align-items-center">

                <Col md={8}>

                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Current Status
                  </p>

                  <Badge
                    bg={
                      locum.profile.approval_status === "APPROVED"
                        ? "success"
                        : locum.profile.approval_status === "REJECTED"
                          ? "danger"
                          : "warning"
                    }
                    className="rounded-pill fs-9"
                  >
                    {locum.profile.approval_status}
                  </Badge>

                  {locum.profile.approval_status === "REJECTED" &&
                    locum.profile.approval_reason && (
                      <>
                        <p className="text-body-tertiary small mt-4 mb-2 text-uppercase fw-bold">
                          Rejection Reason
                        </p>

                        <p className="text-body-secondary mb-0">
                          {locum.profile.approval_reason}
                        </p>
                      </>
                    )}

                </Col>

                <Col
                  md={4}
                  className="text-md-end mt-3 mt-md-0"
                >

                  {locum.profile.approval_status === "PENDING" && (
                    <>
                      <Button
                        variant="phoenix-primary"
                        onClick={() => setShowApproveModal(true)}
                        className="me-2"
                      >
                        Approve
                      </Button>

                      <Button
                        variant="phoenix-danger"
                        onClick={() => setShowRejectModal(true)}
                      >
                        Reject
                      </Button>
                    </>
                  )}

                </Col>

              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Actions */}
      <Row>
        <Col>
          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant={locum.is_active ? "danger" : "success"}
              onClick={handleToggleStatus}
            >
              {locum.is_active ? "Deactivate" : "Activate"} Locum
            </Button>

            <Button
              variant="outline-danger"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete Locum
            </Button>
          </div>
        </Col>
      </Row>
      <Modal
        show={showApproveModal}
        onHide={() => setShowApproveModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Approve Locum
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-2">
            Are you sure you want to approve <strong>{locum.user.name}</strong>?
          </p>
          {/* <p className="text-body-tertiary mb-0">
            This will change the locum approval status to approved and allow them to continue in the workflow.
          </p> */}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="phoenix-secondary"
            onClick={() => setShowApproveModal(false)}
          >
            Cancel
          </Button>

          <Button
            variant="phoenix-primary"
            disabled={approveLoading}
            onClick={handleApprove}
          >
            {approveLoading ? "Approving..." : "Approve Locum"}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Reject Locum
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>

          <Form.Group>

            <Form.Label>
              Rejection Reason
            </Form.Label>

            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Enter rejection reason..."
              value={rejectReason}
              onChange={(e) =>
                setRejectReason(e.target.value)
              }
            />

            <Form.Text className="text-muted">
              This reason will be emailed to the locum.
            </Form.Text>

          </Form.Group>

        </Modal.Body>

        <Modal.Footer>

          <Button
            variant="phoenix-secondary"
            onClick={() => {
              setShowRejectModal(false);
              setRejectReason("");
            }}
          >
            Cancel
          </Button>

          <Button
            variant="phoenix-danger"
            disabled={
              rejectLoading ||
              !rejectReason.trim()
            }
            onClick={handleReject}
          >
            {rejectLoading ? "Rejecting..." : "Reject Locum"}
          </Button>

        </Modal.Footer>
      </Modal>
      <Modal
        show={showDeleteModal}
        onHide={() => !deleteLoading && setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton={!deleteLoading}>
          <Modal.Title>Delete Locum</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-2">
            Are you sure you want to permanently delete this locum?
          </p>

          <div className="alert alert-danger mb-0">
            <strong>Warning:</strong> This will permanently delete the locum account,
            profile, documents, bookings, and all related data. This action cannot be
            undone.
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="phoenix-secondary"
            disabled={deleteLoading}
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>

          <Button
            variant="phoenix-danger"
            disabled={deleteLoading}
            onClick={handleDelete}
          >
            {deleteLoading ? "Deleting..." : "Delete Permanently"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminLocumDetailsPage;
